import { Wrench, Settings, Users, TrendingUp, Megaphone } from 'lucide-react';

export interface DepartmentHistoryItem {
  id: string;
  department: string;
  testsTaken: number;
  lastUpdated?: Date;
}

interface DepartmentTestHistoryProps {
  items: DepartmentHistoryItem[];
}

const iconMap: Record<string, { icon: typeof Wrench; iconBg: string; iconColor: string }> = {
  engineering: { icon: Wrench, iconBg: 'bg-navy-700', iconColor: 'text-gold-400' },
  operations: { icon: Settings, iconBg: 'bg-navy-700', iconColor: 'text-gold-400' },
  hr: { icon: Users, iconBg: 'bg-navy-700', iconColor: 'text-gold-400' },
  sales: { icon: TrendingUp, iconBg: 'bg-green-500/15', iconColor: 'text-green-400' },
  pr: { icon: Megaphone, iconBg: 'bg-orange-500/15', iconColor: 'text-orange-400' },
  default: { icon: Users, iconBg: 'bg-navy-800', iconColor: 'text-navy-200' },
};

const formatRelativeTime = (date?: Date) => {
  if (!date) return 'No data yet';
  const diffMs = Date.now() - date.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  if (diffMinutes < 60) return `${diffMinutes} min ago`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
};

const normalizeDepartmentKey = (department: string) => {
  const lower = department.toLowerCase();
  if (lower.includes('engineer')) return 'engineering';
  if (lower.includes('operation')) return 'operations';
  if (lower.includes('hr') || lower.includes('human')) return 'hr';
  if (lower.includes('sale')) return 'sales';
  if (lower.includes('pr') || lower.includes('public')) return 'pr';
  return 'default';
};

export function DepartmentTestHistory({ items }: DepartmentTestHistoryProps) {
  return (
    <div className="bg-navy-800 rounded-2xl p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-bold text-white">Department Test History</h2>
      </div>

      {items.length === 0 ? (
        <div className="py-12 text-center text-sm text-navy-300">No department activity yet.</div>
      ) : (
        <div className="space-y-4 mb-6">
          {items.map((history) => {
            const key = normalizeDepartmentKey(history.department);
            const iconConfig = iconMap[key] ?? iconMap.default;
            const Icon = iconConfig.icon;
            return (
              <div key={history.id} className="flex items-start gap-3">
                <div className={`${iconConfig.iconBg} ${iconConfig.iconColor} p-2.5 rounded-lg flex-shrink-0`}>
                  <Icon size={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-white mb-0.5">{history.department}</h3>
                  <p className="text-sm text-navy-300">{history.testsTaken} tests completed</p>
                </div>
                <span className="text-xs text-navy-300 flex-shrink-0">{formatRelativeTime(history.lastUpdated)}</span>
              </div>
            );
          })}
        </div>
      )}

    </div>
  );
}
